import {useQuery} from '@tanstack/react-query'
import {useMemo} from 'react'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {X} from 'lucide-react'
import {Link, useSearchParams, useNavigate} from 'react-router-dom'
import {routes} from '@/lib/routes'
import {usePageTitle} from '@/hooks/use-page-title'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {JobExecutionControllerAllExecutionsEndpoint} from '@/types/endpoint-contracts'
import {useExecutionsListStream} from '@/hooks/use-executions-list-stream'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {JobExecutionStatus, JobExecutionFilterParams} from '@/types/background-jobs'
import {formatTimestamp, formatDuration} from '@/lib/date-utils'
import {StatusBadge, StatusBadgeType} from '@/components/shared/StatusBadge'

/**
 * Job Executions list page — Phase C.6 unified search migration (issue 342.11).
 * Replaces the inline status `<Select>` + free-text `<SearchInput>` with
 * {@link UnifiedSearchBar}, driven by
 * {@link JobExecutionControllerAllExecutionsEndpoint}: the bar emits `q`
 * against the backend's `@Searchable errorMessage` field; status, jobId, period
 * and the from/to date range render as typed filter chips and as panel
 * controls.
 *
 * The legacy jobId deep-link from JobDetail still works — it is now
 * routed through the unified bar (the JOB_ID filter chip), preserving the
 * old behaviour while letting users clear it via the standard chip ✕.
 *
 * SSE streaming for live execution updates is gated on whether the user is
 * looking at running rows — same heuristic as before, but resolved against
 * the unified URL state.
 */
export function JobExecutions() {
    const i18n = useI18n()
    const services = useServices()
    const navigate = useNavigate()

    usePageTitle(i18n.jobExecutions.pageTitle)
    const [searchParams, setSearchParams] = useSearchParams()

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: JobExecutionControllerAllExecutionsEndpoint,
        defaultPageSize: 20,
        defaultSortBy: JobExecutionControllerAllExecutionsEndpoint.sort.STARTED_AT,
        defaultSortDir: SortDir.DESC,
    })

    // SSE streaming for live updates when there might be running executions.
    // Driven by the unified URL state (`status` filter, keyed by the
    // endpoint-contract identifier — not the URL param name).
    const statusFilterRaw = searchAndFilters.filters.STATUS ?? ''
    const statusFilter = Array.isArray(statusFilterRaw) ? statusFilterRaw[0] : statusFilterRaw
    useExecutionsListStream({enabled: !statusFilter || statusFilter === JobExecutionStatus.RUNNING})

    const jobIdFilter = searchAndFilters.filters.JOB_ID
    const jobIdValue = useMemo(() => Array.isArray(jobIdFilter) ? jobIdFilter[0] : jobIdFilter, [jobIdFilter])

    const {data: execData, isLoading, error} = useQuery({
        queryKey: ['executions', queryString],
        queryFn: () => services.jobs.executions(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const executions = execData?.content ?? []
    const totalItems = execData?.totalElements ?? 0
    const totalPages = execData?.totalPages ?? 0

    const clearJobFilter = () => {
        const next = new URLSearchParams(searchParams)
        next.delete(JobExecutionFilterParams.jobId)
        setSearchParams(next)
    }

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.jobExecution}
            loading={isLoading}
            error={error}
            title={i18n.jobExecutions.allExecutions as string}
            count={totalItems}
            data-testid="job-executions-page"
            description={hasActiveFilters ? i18n.common.matchingFilters : undefined}
            filters={
                <>
                    <UnifiedSearchBar
                        contract={JobExecutionControllerAllExecutionsEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.jobExecutions.filterPlaceholder as string}
                    />
                    {jobIdValue && (
                        <Badge variant="secondary" className="gap-1">
                            {i18n.jobExecutions.jobLabel} {jobIdValue.slice(0, 8)}
                            <Button
                                variant="ghost"
                                size="sm"
                                data-testid="btn-clear-job-filter"
                                onClick={clearJobFilter}
                                className="ml-1"
                                aria-label={i18n.common.clear}
                            >
                                <X className="h-3 w-3"/>
                            </Button>
                        </Badge>
                    )}
                </>
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-job-name">{i18n.jobExecutions.jobName}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-started">{i18n.jobExecutions.started}</TableHead>
                        <TableHead data-testid="col-duration">{i18n.jobExecutions.duration}</TableHead>
                        <TableHead data-testid="col-tasks">{i18n.jobExecutions.tasks}</TableHead>
                        <TableHead data-testid="col-node">{i18n.jobExecutions.node}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {executions.map((exec) => (
                        <TableRow key={exec.id} data-testid={`row-exec-${exec.id}`}
                                 className="cursor-pointer"
                                 onClick={() => navigate(routes.backgroundJobs.executions.detail(exec.id))}>
                            <TableCell className="font-medium">
                                <Link
                                    to={routes.backgroundJobs.jobs.detail(exec.jobId)}
                                    className="hover:underline text-primary"
                                    data-testid={`link-job-${exec.jobId}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {exec.jobName}
                                </Link>
                            </TableCell>
                            <TableCell>
                                <Link to={routes.backgroundJobs.executions.detail(exec.id)}
                                      data-testid={`link-exec-${exec.id}`}
                                      onClick={(e) => e.stopPropagation()}>
                                    <StatusBadge status={exec.status} type={StatusBadgeType.EXECUTION_STATUS}/>
                                </Link>
                            </TableCell>
                            <TableCell className="text-sm">
                                {formatTimestamp(exec.startedAt)}
                            </TableCell>
                            <TableCell className="text-sm">
                                {formatDuration(exec.durationMs)}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                                {exec.completedTasks}/{exec.totalTasks}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                                {exec.nodeId}
                            </TableCell>
                        </TableRow>
                    ))}
                    {executions.length === 0 && (
                        <TableEmptyState
                            colSpan={6}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.jobExecutions.noExecutionsMatch}
                            emptyMessage={i18n.jobExecutions.noExecutionsFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
